// A slim filter bar shown on every authenticated page (rendered once in
// AuthenticatedLayout). It offers two dropdowns:
//   - RO  (Regional Office)
//   - PIU (Project Implementation Unit), narrowed to the chosen RO
// Selecting a value persists it and reloads so all pages refetch filtered by
// the global axios interceptor (see utils/roPiuFilter.js).
import React, { useEffect, useState } from "react";
import axios from "axios";
import { MdFilterList, MdClose } from "react-icons/md";
import {
  getRoFilter, getPiuFilter, getWeekFilter,
  applyRoPiuFilter, applyWeekFilter, WEEK_KEY, ALL_WEEKS,
} from "../../utils/roPiuFilter";
import useAuth from "../../hooks/useAuth";

const BASE = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");

// Users for whom the RO/PIU filter bar is hidden entirely.
const HIDE_RO_PIU_FILTER_USERS = new Set([
  "vijaywada@roadvision.ai",
]);

const selectStyle = {
  fontSize: 12,
  padding: "4px 8px",
  borderRadius: 6,
  border: "1px solid #d0d5dd",
  background: "#fff",
  color: "#1f2937",
  minWidth: 150,
  maxWidth: 240,
  cursor: "pointer",
};

const RoPiuFilterBar = () => {
  const { user } = useAuth();
  const hideFilter = HIDE_RO_PIU_FILTER_USERS.has((user?.email || "").toLowerCase());
  const [ros, setRos] = useState([]);
  const [roToPius, setRoToPius] = useState({});
  const [allPius, setAllPius] = useState([]);
  const [ro, setRo] = useState(getRoFilter());
  const [piu, setPiu] = useState(getPiuFilter());
  // Weekly-survey filter: list of available weeks (period_label) and the chosen
  // one. Defaults to the latest week (see effect below).
  const [weeks, setWeeks] = useState([]);
  const [week, setWeek] = useState(getWeekFilter());
  // Single-org users (any non-SuperAdmin, or a SuperAdmin who picked an org)
  // are already scoped to one RO server-side, so we hide the RO dropdown and
  // offer only that RO's PIUs. `userRo` empty == SuperAdmin all-orgs view.
  const [userRo, setUserRo] = useState("");
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${BASE}/project/ro_piu_filters`);
        const result = res.data?.result || {};
        if (cancelled) return;
        setRos(result.ros || []);
        setRoToPius(result.ro_to_pius || {});
        setAllPius(result.pius || []);
        setUserRo(result.user_ro || "");
        setIsSuperAdmin(!!result.is_super_admin);
      } catch (e) {
        console.error("Failed to load RO/PIU filters", e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Show the RO dropdown only in the cross-org (SuperAdmin, no org) view.
  const showRoSelect = !userRo;

  // PIUs offered: a single-org user sees only their RO's PIUs; otherwise PIUs
  // narrow to the selected RO, or all PIUs when no RO is chosen.
  const piuOptions = userRo
    ? (roToPius[userRo] || [])
    : (ro && roToPius[ro] ? roToPius[ro] : allPius);

  const onRoChange = (e) => {
    const next = e.target.value;
    setRo(next);
    // Changing RO invalidates a PIU that no longer belongs to it.
    const validPius = next && roToPius[next] ? roToPius[next] : allPius;
    const nextPiu = piu && validPius.includes(piu) ? piu : "";
    applyRoPiuFilter(next, nextPiu);
  };

  // Single-org users never set `ro` (the backend already org-scopes them); they
  // only narrow by PIU.
  const onPiuChange = (e) => {
    applyRoPiuFilter(userRo ? "" : ro, e.target.value);
  };

  const onWeekChange = (e) => applyWeekFilter(e.target.value);

  const clearAll = () => {
    // Also drop the week so the bar re-defaults to the latest week next load.
    try { window.localStorage.removeItem(WEEK_KEY); } catch {}
    applyRoPiuFilter("", "");
  };

  const hasActive = ro || piu;

  // Only show the RO/PIU bar for orgs that are part of the NHAI RO/PIU
  // reference. The backend resolves a non-empty `user_ro` only when the user's
  // organization has surveyed roads present in data/road_reference.json (i.e.
  // the orgs registered under SuperAdmin) — that's the ~marked set. SuperAdmin
  // also gets it (the RO dropdown only works in the cross-org all-orgs view).
  // Every other org has no RO/PIU data, so the bar stays hidden. Defaults to
  // hidden until /ro_piu_filters resolves, so there's no flash for others.
  const showBar = isSuperAdmin || !!userRo;

  // Load the available survey weeks (org + RO/PIU scoped via the axios
  // interceptor) and default to the latest week on first visit. Gated on the bar
  // being shown so denylisted / non-NHAI users never get an invisible filter.
  useEffect(() => {
    if (hideFilter || !showBar) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${BASE}/project/weekly_report_weeks`);
        const list = res.data?.result || [];
        if (cancelled) return;
        setWeeks(list);
        // Default to the latest week (the list is sorted latest-first) and reload
        // once when there's nothing stored yet (first visit) OR the stored week
        // is no longer in scope (e.g. after an RO/PIU change). "All weeks" is
        // kept as an explicit user choice.
        const stored = getWeekFilter();
        const inScope = list.some((w) => w.period_label === stored);
        if (list.length > 0 && stored !== ALL_WEEKS && !inScope) {
          applyWeekFilter(list[0].period_label);
        }
      } catch (e) {
        console.error("Failed to load survey weeks", e);
      }
    })();
    return () => { cancelled = true; };
  }, [hideFilter, showBar]);

  // hideFilter (denylist) kept as an extra hard-hide on top of the org gate.
  if (hideFilter || !showBar) return null;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "6px 16px",
        borderBottom: "1px solid #e5e7eb",
        background: "#f8fafc",
        flexWrap: "wrap",
      }}
    >
      <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 600, color: "#475569" }}>
        <MdFilterList size={16} /> Filters
      </span>

      {showRoSelect ? (
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#475569" }}>
          RO
          <select style={selectStyle} value={ro} onChange={onRoChange}>
            <option value="">All ROs</option>
            {ros.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </label>
      ) : (
        <span style={{ fontSize: 12, color: "#475569" }}>
          RO: <strong style={{ color: "#1f2937" }}>{userRo}</strong>
        </span>
      )}

      <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#475569" }}>
        PIU
        <select style={selectStyle} value={piu} onChange={onPiuChange}>
          <option value="">All PIUs</option>
          {piuOptions.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
      </label>

      {weeks.length > 0 && (
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#475569" }}>
          Weekly Survey
          <select style={selectStyle} value={week} onChange={onWeekChange}>
            <option value={ALL_WEEKS}>All weeks</option>
            {weeks.map((w) => (
              <option key={w.period_label} value={w.period_label}>
                {w.period_label}{w.road_count ? ` (${w.road_count} road${w.road_count !== 1 ? "s" : ""})` : ""}
              </option>
            ))}
          </select>
        </label>
      )}

      {hasActive && (
        <button
          onClick={clearAll}
          style={{
            display: "flex", alignItems: "center", gap: 4, fontSize: 12,
            color: "#b91c1c", background: "transparent", border: "none", cursor: "pointer",
          }}
          title="Clear RO/PIU filters"
        >
          <MdClose size={14} /> Clear
        </button>
      )}
    </div>
  );
};

export default RoPiuFilterBar;
